// src/pages/MockPaywall.jsx
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PLANS } from "../config/payments";

/**
 * MockPaywall.jsx (V1.1 Beta)
 * ------------------------------------------------------------
 * Purpose:
 * - Testing-only paywall that appears after Level-1.
 * - Shows pricing plans for perceived value testing.
 * - Does NOT trigger Razorpay or any subscription side-effects.
 * - Collects a lightweight fun check-in + inline note + opens Google Form.
 *
 * Notes:
 * - We prefill ONLY your paragraph question (the last para field in your form).
 */

// 🔗 Base Google Form URL (no query params)
const FEEDBACK_FORM_BASE_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSdxA7Lex1dEGBh2G2M_lc6-znknH7oVCfD_l0Z-zyKfh86ibw/viewform";

// 📝 Google Form field ID for the paragraph answer (confirmed by your TEST PREFILL link)
const ENTRY_PARAGRAPH_ID = "entry.1787727835";

// Default "next" destination after mock gate
const DEFAULT_NEXT_ROUTE = "/level-2-beta";

// Optional: localStorage key to store quick beta signal
const FUN_SIGNAL_KEY = "tt_v11_fun_signal";

// Assets
const DORON_SLEEP_SRC = "/images/Sleeping Doron.png";

function useQuery(search) {
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function MockPaywall() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const query = useQuery(search);

  // Where should "Continue (Beta Access)" take them?
  const next = query.get("next") || DEFAULT_NEXT_ROUTE;

  // Lightweight fun signal
  const [funAnswer, setFunAnswer] = useState("");

  // Inline quick feedback text (feeds into paragraph question)
  const [inlineFeedback, setInlineFeedback] = useState("");

  // Toast when feedback button is clicked
  const [showToast, setShowToast] = useState(false);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(FUN_SIGNAL_KEY);
      if (existing) setFunAnswer(existing);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    try {
      if (funAnswer) localStorage.setItem(FUN_SIGNAL_KEY, funAnswer);
    } catch {
      // ignore
    }
  }, [funAnswer]);

  useEffect(() => {
    if (!showToast) return;
    const t = setTimeout(() => setShowToast(false), 2800);
    return () => clearTimeout(t);
  }, [showToast]);

  // Safe amount formatting
  const dayAmount =
    PLANS?.DAY_PASS?.amount != null
      ? Math.round(PLANS.DAY_PASS.amount / 100)
      : null;

  const weekAmount =
    PLANS?.WEEK_PASS?.amount != null
      ? Math.round(PLANS.WEEK_PASS.amount / 100)
      : null;

  const monthlyAmount =
    PLANS?.MONTHLY_INR?.amount != null
      ? Math.round(PLANS.MONTHLY_INR.amount / 100)
      : null;

  // Build a pre-filled Google Form URL:
  // - paragraph field gets inlineFeedback + funAnswer + source + timestamp
  // - plus UTM params for click attribution (optional)
  const buildFeedbackUrl = () => {
    const url = new URL(FEEDBACK_FORM_BASE_URL);

    const timestamp = new Date().toISOString();

    const lines = [];
    if (inlineFeedback.trim()) {
      lines.push(`Inline feedback: ${inlineFeedback.trim()}`);
    }
    if (funAnswer) {
      lines.push(`Fun answer on paywall: ${funAnswer}`);
    }
    lines.push("Source: paywall");
    lines.push(`Timestamp: ${timestamp}`);

    const paragraphValue = lines.join("\n");

    url.searchParams.set(ENTRY_PARAGRAPH_ID, paragraphValue);

    // UTM tags (optional; helpful if you later parse or track)
    url.searchParams.set("utm_source", "treble-town");
    url.searchParams.set("utm_medium", "paywall");
    url.searchParams.set("utm_campaign", "v1-1-beta");

    // Ensures we are using a "pre-fill friendly" view
    // (Google adds usp=pp_url sometimes, but it's not required for prefilling)
    // Keeping it out avoids double params; Google still accepts entry.x fields.
    return url.toString();
  };

  const openFeedbackForm = () => {
    window.open(buildFeedbackUrl(), "_blank", "noopener,noreferrer");
    setShowToast(true);
  };

  const handleContinue = () => navigate(next, { replace: true });
  const handleBackToLevel1 = () => navigate("/level-1");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-white to-purple-100 p-4">
      <div className="w-full max-w-4xl bg-white/95 rounded-2xl p-6 md:p-10 shadow-lg border relative overflow-hidden">
        {/* Toast */}
        {showToast && (
          <div className="absolute bottom-4 inset-x-0 flex justify-center px-4 pointer-events-none z-20">
            <div className="pointer-events-auto rounded-full bg-emerald-600 text-white text-xs sm:text-sm px-4 py-2 shadow-lg">
              Feedback form opened — your note is pre-filled ✅
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700 border border-purple-100">
            V1.1 Beta • Mock Gate (No Payments)
          </div>

          {/* Doron BIG */}
          <div className="mt-5 flex justify-center">
            <img
              src={DORON_SLEEP_SRC}
              alt="Doron sleeping"
              className="w-64 h-64 sm:w-[420px] sm:h-[420px] object-contain drop-shadow-xl"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </div>

          <h1 className="mt-2 text-3xl md:text-4xl font-extrabold">
            You put Doron to sleep! 🌙✨
          </h1>

          <p className="text-gray-600 mt-2 max-w-2xl mx-auto">
            Next up: help Zeb the Zebra celebrate his birthday and collect your
            first Star.
          </p>

          <p className="text-xs text-gray-500 mt-4">
            This page is only for beta testing flow & pricing perception.
          </p>
        </div>

        {/* Plans */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Daily */}
          <div className="rounded-2xl border border-amber-200/60 p-6 shadow-sm text-center bg-gradient-to-b from-amber-50 to-white">
            <div className="text-lg font-semibold">Daily Pass</div>
            <div className="mt-2 text-2xl font-bold">
              {dayAmount != null ? `₹${dayAmount}` : "₹—"}
            </div>
            <div className="text-gray-500">24 hours</div>
            <button
              type="button"
              className="mt-4 w-full rounded-xl px-4 py-2 font-semibold border bg-white/70 text-gray-500 cursor-not-allowed"
              disabled
              title="Mock gate (payments disabled)"
            >
              Get Daily (Beta)
            </button>
          </div>

          {/* Weekly */}
          <div className="rounded-2xl border border-purple-200 p-6 shadow-sm text-center bg-gradient-to-b from-purple-50 to-white">
            <div className="text-lg font-semibold">Weekly Pass</div>
            <div className="mt-2 text-2xl font-bold">
              {weekAmount != null ? `₹${weekAmount}` : "₹—"}
            </div>
            <div className="text-gray-500">7 days</div>
            <button
              type="button"
              className="mt-4 w-full rounded-xl px-4 py-2 font-semibold border bg-white/70 text-gray-500 cursor-not-allowed"
              disabled
              title="Mock gate (payments disabled)"
            >
              Get Weekly (Beta)
            </button>
            <div className="mt-3 text-xs text-purple-700 font-semibold">
              Most popular (testing)
            </div>
          </div>

          {/* Monthly */}
          <div className="rounded-2xl border border-sky-200/60 p-6 shadow-sm text-center bg-gradient-to-b from-sky-50 to-white">
            <div className="text-lg font-semibold">Monthly</div>
            <div className="mt-2 text-2xl font-bold">
              {monthlyAmount != null ? `₹${monthlyAmount}` : "₹—"}
            </div>
            <div className="text-gray-500">
              {monthlyAmount != null ? "30 days" : "Coming Soon"}
            </div>
            <button
              type="button"
              className="mt-4 w-full rounded-xl px-4 py-2 font-semibold border bg-white/70 text-gray-500 cursor-not-allowed"
              disabled
              title="Mock gate (payments disabled)"
            >
              {monthlyAmount != null ? "Get Monthly (Beta)" : "Coming Soon"}
            </button>
          </div>
        </div>

        {/* Fun + feedback */}
        <div className="mt-10 rounded-2xl border bg-white p-5 md:p-6">
          <h2 className="text-lg md:text-xl font-extrabold">Quick Beta Check</h2>
          <p className="text-gray-600 text-sm mt-1">
            One tiny answer helps us improve the experience before Level-2.
          </p>

          <div className="mt-4">
            <div className="font-semibold">Did you have fun in Treble Town?</div>

            <div className="mt-2 flex items-center gap-6">
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tt-fun"
                  value="Yes"
                  checked={funAnswer === "Yes"}
                  onChange={() => setFunAnswer("Yes")}
                  className="accent-purple-600"
                />
                <span>Yes</span>
              </label>

              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="tt-fun"
                  value="No"
                  checked={funAnswer === "No"}
                  onChange={() => setFunAnswer("No")}
                  className="accent-purple-600"
                />
                <span>No</span>
              </label>
            </div>

            <div className="mt-4">
              <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
                Any quick suggestion (optional)?
              </label>
              <textarea
                rows={3}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Type a quick note. This will be pre-filled into the form."
                value={inlineFeedback}
                onChange={(e) => setInlineFeedback(e.target.value)}
              />
            </div>

            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={openFeedbackForm}
                className="rounded-xl px-5 py-3 font-bold bg-purple-600 text-white hover:bg-purple-700 transition"
              >
                Open Feedback Form
              </button>

              <button
                type="button"
                onClick={handleContinue}
                className="rounded-xl px-5 py-3 font-bold border border-gray-200 bg-white hover:bg-gray-50 transition"
                title="Mock gate: continue without payment for beta testing"
              >
                Continue to Level-2 (Beta)
              </button>
            </div>

            <div className="mt-3">
              <button
                type="button"
                onClick={handleBackToLevel1}
                className="text-xs sm:text-sm font-semibold text-slate-500 hover:text-slate-800 hover:underline"
              >
                ← Back to Level-1 to try the melody again
              </button>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="text-center text-xs text-gray-400 mt-8">
          Treble Town V1.1 Beta • Mock Gate only • No payments collected
        </p>
      </div>
    </div>
  );
}
